import React from 'react';
import type { Organism } from '@verndale/core';
import globModules from './glob-modules';
import { renderElement } from './helpers/react-18-shim';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const defaultQueryClient = new QueryClient();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reactModule = (Component: any, nodeList: NodeListOf<HTMLElement>) => {
  return nodeList.forEach(node => {
    renderElement(
      <QueryClientProvider client={defaultQueryClient}>
        <Component {...node.dataset} />
      </QueryClientProvider>,
      node
    );
  });
};

const modules: Organism[] = [
  {
    name: 'accordion',
    loader: () => import('./modules/accordion')
  },
  {
    name: 'audioClip',
    loader: () => import('./modules/audioClip/index')
  },
  {
    name: 'mockApi',
    styles: () => import('../scss/modules/mock-api.scss'),
    loader: () => import('./modules/mockApi'),
    render: reactModule
  },
  {
    name: 'mockApiGraphQL',
    styles: () => import('../scss/modules/mock-api.scss'),
    loader: () => import('./modules/mockApiGraphQL'),
    render: reactModule
  },
  {
    name: 'quoteCard',
    loader: () => import('./modules/quoteCard')
  },
  {
    name: 'header',
    loader: () => import('./modules/header')
  },
  {
    name: 'videoModule',
    loader: () => import('./modules/videoModule')
  },
  {
    name: 'logoContainer',
    loader: () => import('./modules/logoContainer')
  },
  {
    name: 'imagesCarousel',
    styles: () => import('../scss/modules/images-carousel.scss'),
    loader: () => import('./modules/imagesCarousel')
  },
  {
    name: 'verticalTabbedContainer',
    styles: () => import('../scss/modules/vertical-tabbed-container.scss'),
    loader: () => import('./modules/tabs')
  },
  {
    name: 'horizontalTabsWithCtas',
    styles: () => import('../scss/modules/horizontal-tabs-with-ctas.scss'),
    loader: () => import('./modules/tabs')
  },
  {
    name: 'mediaMasthead',
    loader: () => import('./modules/mediaMasthead')
  },
  {
    name: 'media-masthead-transparent',
    loader: () => import('./modules/media-masthead-transparent')
  },
  {
    name: 'media-masthead-textural',
    loader: () => import('./modules/media-masthead-textural')
  },  
  {
    name: 'media-masthead-RFI',
    loader: () => import('./modules/media-masthead-RFI')
  },
  {
    name: 'scrollIndicator',
    loader: () => import('./modules/scrollIndicator')
  },
  {
    name: 'modalVideoModule',
    loader: () => import('./modules/modalVideoModule')
  },
  {
    name: 'modal',
    loader: () => import('./modules/modal')
  },
  {
    name: 'genericCarousel',
    loader: () => import('./modules/genericCarousel')
  },
  {
    name: 'audioCard',
    loader: () => import('./modules/audioCard')
  },
  {
    name: 'audienceQuestionnarie',
    loader: () => import('./modules/audienceQuestionnarie'),
    styles: () => import('../scss/modules/audience-questionnarie.scss')
  },
  {
    name: 'continuousCarousel',
    loader: () => import('./modules/continuousCarousel')
  },
  {
    name: 'slidingCardContainer',
    loader: () => import('./modules/slidingCardContainer')
  },
  {
    name: 'shareCard',
    loader: () => import('./modules/shareCard')
  },
  {
    name: 'dropdown',
    loader: () => import('./modules/dropdown')
  },
  {
    name: 'testimonialCardsCarousel',
    loader: () => import('./modules/testimonialCardsCarousel')
  },
  {
    name: 'productImagesCarousel',
    loader: () => import('./modules/productImagesCarousel')
  },
  {
    name: 'jumplinks',
    loader: () => import('./modules/jumplinks'),
    styles: () => import('../scss/modules/jumplinks.scss')
  },
  {
    name: 'latestNewsGraphQL',
    loader: () => import('./modules/react-modules/LatestNewsGraphQL'),
    render: reactModule
  },
  {
    name: 'featuredEventsGraphQL',
    loader: () => import('./modules/react-modules/FeaturedEventsModuleGraphQL'),
    render: reactModule
  },
  {
    name: 'courseScheduleSelector',
    styles: () => import('../scss/modules/course-schedule-selector.scss'),
    loader: () => import('./modules/courseScheduleSelector')
  },
  {
    name: 'rtfItemsModule',
    loader: () => import('./modules/rtfItemsModule')
  },
  {
    name: 'audioButton',
    loader: () => import('./modules/audioButton')
  },
  {
    name: 'peopleDetailMasthead',
    loader: () => import('./modules/peopleDetailMasthead')
  },
  {
    name: 'relatedBlogsAndNewsSlider',
    loader: () => import('./modules/relatedBlogsAndNewsSlider')
  },
  {
    name: 'relatedBlogsAndNewsSliderGraphQL',
    loader: () => import('./modules/react-modules/RelatedBlogsAndNewsSliderGraphQL'),
    render: reactModule
  },
  {
    name: 'shareModule',
    loader: () => import('./modules/shareModule')
  },
  {
    name: 'eventsListingModule',
    loader: () => import('./modules/react-modules/Listings/EventListing'),
    render: reactModule
  },
  {
    name: 'resourceListingModule',
    loader: () => import('./modules/react-modules/Listings/ResourcesListing'),
    render: reactModule
  },
  {
    name: 'officeAndServicesListingModule',
    loader: () => import('./modules/react-modules/Listings/OfficeAndServicesListing'),
    render: reactModule
  },
  {
    name: 'blogAndNewsListing',
    loader: () => import('./modules/react-modules/Listings/BlogAndNewsListing'),
    render: reactModule
  },
  {
    name: 'searchListing',
    loader: () => import('./modules/react-modules/Listings/SearchListing'),
    render: reactModule
  },
  {
    name: 'programsAndCertificatesListing',
    loader: () => import('./modules/react-modules/Listings/ProgramsAndCertificatesListing'),
    render: reactModule
  },
  {
    name: 'eCommerceCoursesAndProductsListingModule',
    loader: () => import('./modules/react-modules/Listings/ECommerceCoursesAndProductsListing'),
    render: reactModule
  },
  {
    name: 'peopleDirectoryListingModule',
    loader: () => import('./modules/react-modules/Listings/PeopleDirectoryListing'),
    render: reactModule
  },
  {
    name: 'loginForm',
    loader: () => import('./modules/loginForm')
  },
  {
    name: 'passwordInput',
    loader: () => import('./modules/passwordInput')
  },
  {
    name: 'reactAccordion',
    loader: () => import('./modules/react-modules/Accordion'),
    render: reactModule
  },
  {
    name: 'alertBanner',
    loader: () => import('./modules/alertBanner')
  },
  {
    name: 'shippingForm',
    loader: () => import('./modules/shippingForm')
  },
  {
    name: 'changePasswordForm',
    loader: () => import('./modules/changePasswordForm')
  },
  {
    name: 'dateInput',
    loader: () => import('./modules/dateInput')
  },
  {
    name: 'emailInput',
    loader: () => import('./modules/emailInput')
  },
  {
    name: 'customForm',
    loader: () => import('./modules/customForm')
  },
  {
    name: 'billingForm',
    loader: () => import('./modules/billingForm')
  },
  {
    name: 'relatedProductSlider',
    loader: () => import('./modules/relatedProductsSlider'),
    styles: () => import('../scss/modules/related-products-slider.scss')
  },
  {
    name: 'relatedResourcesSlider',
    loader: () => import('./modules/relatedResourcesSlider'),
    styles: () => import('../scss/modules/related-resources-slider.scss')
  },
  {
    name: 'relatedOfficesAndNewsSlider',
    loader: () => import('./modules/relatedOfficesAndNewsSlider')
  },
  {
    name: 'relatedOfficesAndNewsSliderGraphQL',
    loader: () => import('./modules/react-modules/RelatedOfficesAndNewsSliderGraphQL'),
    render: reactModule
  },
  {
    name: 'relatedResourcesSliderGraphQL',
    loader: () => import('./modules/react-modules/RelatedResourcesSliderGraphQL'),
    styles: () => import('../scss/modules/related-resources-slider.scss'),
    render: reactModule
  },
  {
    name: 'blogNewsBar',
    loader: () => import('./modules/blogNewsBar')
  },
  {
    name: 'toastNotification',
    loader: () => import('./modules/toastNotification')
  },
  {
    name: 'serverAlertBanner',
    loader: () => import('./modules/serverAlertBanner')
  },
  {
    name: 'accountMenu',
    loader: () => import('./modules/accountMenu')
  },
  {
    name: 'checkoutSteps',
    loader: () => import('./modules/checkoutSteps')
  },
  {
    name: 'loginHeader',
    loader: () => import('./modules/loginHeader')
  },
  {
    name: 'stickyBar',
    loader: () => import('./modules/stickyBar')
  },
  {
    name: 'invoiceDetail',
    loader: () => import('./modules/invoiceDetail')
  },
  {
    name: 'coursesDashboard',
    loader: () => import('./modules/coursesDashboard')
  },
  {
    name: 'productDetail',
    loader: () => import('./modules/productDetail')
  },
  {
    name: 'employeeBenefits',
    loader: () => import('./modules/employeeBenefits')
  },
  {
    name: 'cart',
    loader: () => import('./modules/react-modules/Cart'),
    render: reactModule
  },
  {
    name: 'iconSections',
    loader: () => import('./modules/iconSections')
  },
  {
    name: 'tableCardContainer',
    loader: () => import('./modules/tableCardContainer')
  },
  {
    name: 'statCard',
    loader: () => import('./modules/statCard')
  },
  {
    name: 'gallery',
    styles: () => import('../scss/modules/gallery.scss'),
    loader: () => import('./modules/gallery')
  },
  {
    name: 'galleryGrid',
    styles: () => import('../scss/modules/gallery-grid.scss'),
    loader: () => import('./modules/galleryGrid')
  },
  {
    name: 'countdownTimer',
    styles: () => import('../scss/modules/countdown-timer.scss'),
    loader: () => import('./modules/countdownTimer')
  }
];

export default [...globModules, ...modules];
